import { motion, AnimatePresence } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { Sparkles, MessageCircle } from 'lucide-react'
import type { BriefingConfig } from '../../../shared/types/briefing'
import { expandCollapse } from '@tendbot/ui'
import { AnimatedChevron } from '@tendbot/ui'

interface LastBriefingCardProps {
  lastBriefing: NonNullable<BriefingConfig['lastBriefing']>
  isExpanded: boolean
  onToggleExpand: () => void
}

export default function LastBriefingCard({ lastBriefing, isExpanded, onToggleExpand }: LastBriefingCardProps) {
  const { t, i18n } = useTranslation('briefing')
  return (
    <div
      onClick={onToggleExpand}
      className="glass-card rounded-2xl overflow-hidden cursor-pointer glow-gold relative"
    >
      <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-gold/20 to-purple/10 rounded-full blur-3xl" />

      <div className="relative p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gold/20 flex items-center justify-center">
            <Sparkles size={20} className="text-gold" />
          </div>
          <div>
            <p className="text-ivory font-medium">{t('last.title')}</p>
            <p className="text-xs text-pearl/50 mt-0.5">
              {new Date(lastBriefing.timestamp).toLocaleDateString(i18n.language, {
                weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
              })}
            </p>
          </div>
        </div>
        <AnimatedChevron isOpen={isExpanded} size={16} />
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            {...expandCollapse}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="relative px-5 pb-5 pt-0 border-t border-white/5 space-y-4">
              <p className="text-sm text-pearl/70 pt-3 leading-relaxed italic">
                "{lastBriefing.summary}"
              </p>

              <div className="flex flex-wrap gap-2">
                <span className="text-xs px-3 py-1.5 rounded-full bg-gold/10 text-gold/80">
                  {t('last.eventsCount', { count: lastBriefing.stats.calendarEvents })}
                </span>
                <span className="text-xs px-3 py-1.5 rounded-full bg-gold/10 text-gold/80">
                  {t('last.emailsCount', { count: lastBriefing.stats.emailsProcessed })}
                </span>
                <span className="text-xs px-3 py-1.5 rounded-full bg-gold/10 text-gold/80">
                  {t('last.approvalsCount', { count: lastBriefing.stats.pendingApprovals })}
                </span>
              </div>

              <button className="w-full py-2.5 rounded-full bg-gold/10 text-gold text-sm font-medium hover:bg-gold/20 transition-colors flex items-center justify-center gap-2">
                <MessageCircle size={14} />
                {t('last.viewInChat')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
